// Oturum hareketsizlik takipçisi — uyarı modalı ve auto-logout
'use client';

import { useCallback } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useIdleTimeout } from '../../hooks/use-idle-timeout';

interface IdleTimeoutWatcherProps {
  /** Hareketsizlik süresi (dakika). Default 15. */
  idleMinutes?: number;
  /** Çıkışa kaç saniye kala uyarı gösterilsin. Default 60. */
  warnSeconds?: number;
  /** Süre dolduğunda ya da kullanıcı "Şimdi Çıkış" dediğinde çağrılır. */
  onLogout: () => void;
  /**
   * Cross-tab senkron kanalı — aynı panelin farklı sekmeleri birbirini
   * etkilesin ama admin ile superadmin birbirini etkilemesin.
   * Örn: 'opop-admin-idle' / 'opop-superadmin-idle'
   */
  channelName: string;
}

export function IdleTimeoutWatcher({
  idleMinutes = 15,
  warnSeconds = 60,
  onLogout,
  channelName,
}: IdleTimeoutWatcherProps) {
  const idleMs = idleMinutes * 60_000;
  const warnMs = warnSeconds * 1000;

  // Logout + client-side fallback (server action başarısız olursa)
  const safeLogout = useCallback(() => {
    try { onLogout(); } catch { /* server action hatası */ }
    // Server action yönlendirmezse 2sn sonra client-side redirect
    setTimeout(() => {
      window.location.href = '/login?reason=idle';
    }, 2000);
  }, [onLogout]);

  const handleWarn = useCallback(() => {
    // Hook zaten warning state'ini true yaptı
  }, []);

  const { warning, remainingMs, reset } = useIdleTimeout({
    idleMs,
    warnMs,
    onWarn: handleWarn,
    onTimeout: safeLogout,
    channelName,
  });

  const remainingSec = Math.ceil(remainingMs / 1000);
  const expired = remainingSec <= 0;

  return (
    <Dialog open={warning} onOpenChange={(o) => { if (!o && !expired) reset(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {expired ? 'Oturum sona erdi' : 'Oturumun sona eriyor'}
          </DialogTitle>
          <DialogDescription>
            {expired ? (
              'Oturumunuz sona erdi, çıkış yapılıyor…'
            ) : (
              <>
                Güvenlik için <strong>{remainingSec}</strong> saniye sonra çıkışa
                zorlanacaksınız. Hâlâ burada mısın?
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={safeLogout}>
            Şimdi Çıkış Yap
          </Button>
          {!expired && (
            <Button onClick={reset}>Devam Et</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
